import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { FiChevronDown, FiChevronUp, FiX } from "react-icons/fi";
import { useAuth } from "../../../auth/AuthProvider";
import { Button } from "../../../components";
import { LocationPicker, LocationData } from "../../projects/components/LocationPicker";
import { createLoad, queueLoadRun } from "../../../api/loads";
import { LoadProfileUpload } from "../../load-portfolio/components/LoadProfileUpload";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  isAdmin?: boolean;
  afterCreate?: (loadId: string) => void;
}

/**
 * Create a Load — the RE-Project dual.
 *
 * Same definition flow as a Project: location (map picker), size, existing
 * vs. future + COD, profile choice. Offsets (RE/BESS/NG) are configured on
 * the detail page, not at create time. A pure grid-tied load with zero
 * offsets is the canonical hyperscaler case.
 */
export function LoadCreateModal({
  isOpen,
  onClose,
  customerId,
  isAdmin = false,
  afterCreate,
}: Props) {
  const { token } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();

  const [loadName, setLoadName] = useState("");
  const [description, setDescription] = useState("");
  const [latitude, setLatitude] = useState<number | "">("");
  const [longitude, setLongitude] = useState<number | "">("");
  const [state, setState] = useState<string>("");
  const [county, setCounty] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [timezone, setTimezone] = useState<string>("");
  const [eiaBaCode, setEiaBaCode] = useState<string>("");
  const [nercCode, setNercCode] = useState<string>("");
  const [peakLoadMw, setPeakLoadMw] = useState<number | "">("");
  const [pue, setPue] = useState<number>(1.2);
  const [loadProfile, setLoadProfile] = useState<number[] | null>(null);
  const [loadProfileHourly, setLoadProfileHourly] = useState<
    { timestamp: string; value: number }[] | null
  >(null);
  const [existing, setExisting] = useState(false);
  const [codDate, setCodDate] = useState<string>("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleLocation = (d: LocationData) => {
    setLatitude(d.latitude);
    setLongitude(d.longitude);
    if (d.state) setState(d.state);
    if (d.county) setCounty(d.county);
    if (d.city) setCity(d.city);
    if (d.timezone) setTimezone(d.timezone);
    if (d.baCode) setEiaBaCode(d.baCode);
    if (d.nercRegion) setNercCode(d.nercRegion);
  };

  const handleSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    if (!token) return;
    if (!loadName.trim()) return setError("Load name is required");
    if (latitude === "" || longitude === "") return setError("Location is required (pick on map)");
    if (peakLoadMw === "" || Number(peakLoadMw) <= 0)
      return setError("Peak load (MW) must be > 0");
    if (existing && !codDate) return setError("Existing loads need a COD date");

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        customer_id: isAdmin ? customerId : undefined,
        load_name: loadName.trim(),
        description: description.trim() || null,
        latitude: Number(latitude),
        longitude: Number(longitude),
        state: state || null,
        county: county || null,
        city: city || null,
        timezone: timezone || null,
        eia_ba_code: eiaBaCode || null,
        project_nerc_code: nercCode || null,
        existing_load_flag: existing,
        cod_date: existing && codDate ? new Date(codDate).toISOString() : null,
        peak_load_mw: Number(peakLoadMw),
        pue,
        // A custom shape overrides the flat profile; the engine still scales it
        // by PUE, so upload the IT load (pre-PUE), same as flat. A TIMESTAMPED
        // profile (load_profile_hourly) is self-describing — its timestamps
        // define the analysis period, so no scenario_year is picked; a bare
        // list (load_profile_json) uses positional alignment + scenario_year.
        sam_profile_flag: !loadProfile && !loadProfileHourly,
        load_profile_json: loadProfileHourly ? null : loadProfile,
        load_profile_hourly: loadProfileHourly,
      };
      const created = await createLoad(payload, token);
      // Queue an initial run so the result page has something to render
      try {
        await queueLoadRun(created.load_id, token);
      } catch (q) {
        console.warn("Initial run queue failed (load was created):", q);
      }
      qc.invalidateQueries({ queryKey: ["loads"] });
      onClose();
      if (afterCreate) afterCreate(created.load_id);
      else nav(`/app/loads/${created.load_id}`);
    } catch (e: any) {
      setError(e?.message || "Failed to create load");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface-700 rounded-lg shadow-xl border border-border w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-medium text-text-primary">Create Load</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary">
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded p-3">
              {error}
            </div>
          )}

          {/* Basics */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
              Basics
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2">
                <span className="block text-xs text-text-muted mb-1">Load Name</span>
                <input
                  className="w-full bg-surface-600 border border-border rounded px-3 py-2 text-text-primary"
                  value={loadName}
                  onChange={(e) => setLoadName(e.target.value)}
                  placeholder="e.g. AWS US-East datacenter cluster A"
                  required
                />
              </label>
              <label className="col-span-2">
                <span className="block text-xs text-text-muted mb-1">
                  Description (optional)
                </span>
                <textarea
                  className="w-full bg-surface-600 border border-border rounded px-3 py-2 text-text-primary text-sm"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </label>
              <label>
                <span className="block text-xs text-text-muted mb-1">Peak Load (MW)</span>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  className="w-full bg-surface-600 border border-border rounded px-3 py-2 text-text-primary"
                  value={peakLoadMw}
                  onChange={(e) =>
                    setPeakLoadMw(e.target.value === "" ? "" : Number(e.target.value))
                  }
                  required
                />
              </label>
              <label>
                <span className="block text-xs text-text-muted mb-1">PUE</span>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  max="3"
                  className="w-full bg-surface-600 border border-border rounded px-3 py-2 text-text-primary"
                  value={pue}
                  onChange={(e) => setPue(Number(e.target.value))}
                />
              </label>
            </div>
          </section>

          {/* Load shape */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
              Load Shape
            </h3>
            <p className="text-xs text-text-muted mb-2">
              {loadProfileHourly
                ? "Using a timestamped hourly profile. Its timestamps define the analysis period — the grid is assessed against that calendar year, no year is picked (parity with a generation project's uploaded profile)."
                : loadProfile
                ? "Using an uploaded 8760-hour profile. Grid attribution follows the real hourly shape."
                : "Flat profile from peak load × PUE. Upload an 8760-hour CSV for a real hourly shape — marginal grid intensity is strongly time-of-day dependent, so the shape materially changes the footprint. Add a timestamp column to have the profile's own dates define the analysis period."}
            </p>
            <LoadProfileUpload
              currentFlatLoad={peakLoadMw === "" ? null : Number(peakLoadMw)}
              currentProfile={loadProfile}
              onUpload={(profile) => {
                setLoadProfile(profile);
                setLoadProfileHourly(null);
              }}
              onUploadTimestamped={(entries) => {
                setLoadProfileHourly(entries);
                setLoadProfile(null);
              }}
              onClearProfile={() => {
                setLoadProfile(null);
                setLoadProfileHourly(null);
              }}
            />
          </section>

          {/* Existing vs future */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
              Operating Period
            </h3>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-text-primary">
                <input
                  type="radio"
                  checked={!existing}
                  onChange={() => setExisting(false)}
                />
                Future (model against last full year's grid)
              </label>
              <label className="flex items-center gap-2 text-sm text-text-primary">
                <input
                  type="radio"
                  checked={existing}
                  onChange={() => setExisting(true)}
                />
                Existing (model against actual grid from COD)
              </label>
            </div>
            {existing && (
              <label className="block mt-2 max-w-xs">
                <span className="block text-xs text-text-muted mb-1">
                  Commercial Operation Date (COD)
                </span>
                <input
                  type="date"
                  className="w-full bg-surface-600 border border-border rounded px-3 py-2 text-text-primary"
                  value={codDate}
                  onChange={(e) => setCodDate(e.target.value)}
                  required
                />
              </label>
            )}
          </section>

          {/* Location */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
              Location
            </h3>
            <LocationPicker
              latitude={latitude as number}
              longitude={longitude as number}
              onChange={handleLocation}
            />
            <div className="mt-2 grid grid-cols-4 gap-2 text-xs text-text-muted">
              {state && <span>{state}</span>}
              {county && <span>{county}</span>}
              {eiaBaCode && <span>BA: {eiaBaCode}</span>}
              {nercCode && <span>NERC: {nercCode}</span>}
            </div>
          </section>

          {/* Advanced */}
          <section>
            <button
              type="button"
              className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? <FiChevronUp /> : <FiChevronDown />} Advanced (overrides)
            </button>
            {showAdvanced && (
              <div className="grid grid-cols-2 gap-3 mt-2">
                <label>
                  <span className="block text-xs text-text-muted mb-1">
                    Grid Region (US BA or EU/UK country)
                  </span>
                  <input
                    className="w-full bg-surface-600 border border-border rounded px-3 py-2 text-text-primary"
                    value={eiaBaCode}
                    onChange={(e) => setEiaBaCode(e.target.value)}
                    placeholder="US: ERCO, PJM, CISO · EU/UK: DE, FR, GB"
                  />
                </label>
                <label>
                  <span className="block text-xs text-text-muted mb-1">NERC Code</span>
                  <input
                    className="w-full bg-surface-600 border border-border rounded px-3 py-2 text-text-primary"
                    value={nercCode}
                    onChange={(e) => setNercCode(e.target.value)}
                  />
                </label>
                <label>
                  <span className="block text-xs text-text-muted mb-1">Timezone</span>
                  <input
                    className="w-full bg-surface-600 border border-border rounded px-3 py-2 text-text-primary"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                  />
                </label>
              </div>
            )}
          </section>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create & Run"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
